import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useAction } from '../components/toast'
import { Badge, Card, Empty, Kpi, Loaded, Notice, Page, ReasonDialog, Table } from '../components/ui'
import type { Column } from '../components/ui'
import { del, get, post } from '../lib/api'
import { P, useAuth } from '../lib/auth'
import { count, dateTime, minor, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Plan = {
  id: string
  name: string
  price_minor: number
  currency: string
  interval: string
  inclusions: string[]
  active: boolean
}

type CoachDetailData = {
  id: string
  user_id: string
  display_name: string
  speciality: string
  status: string
  credential: string
  bio: string
  languages: string[]
  service_area: string
  tags: string[]
  years_experience: number
  rating: number
  review_count: number
  active_clients: number
  accepting_clients: boolean
  client_cap: number
  verified_at: string | null
  listing_paid_until: string | null
  created_at: string | null
  account: { email: string; display_name: string; status: string }
  plans: Plan[]
  subscriptions_by_status: Record<string, number>
}

type Review = {
  id: string
  rating: number
  body: string
  reply: string
  months_subscribed: number
  created_at: string | null
}

type Tone = 'alert' | 'cyan' | 'green' | 'lime' | 'orange'

const STATUS_TONE: Record<string, Tone> = {
  live: 'green',
  draft: 'orange',
  delisted: 'lime',
  suspended: 'alert',
}

export function CoachDetail() {
  const { coachId = '' } = useParams()
  const { permissions } = useAuth()
  const canModerate = permissions.has(P.marketplaceModerate)
  const run = useAction()

  const [dialog, setDialog] = useState<'verify' | 'suspend' | 'reinstate' | null>(null)
  const [removing, setRemoving] = useState<Review | null>(null)

  const coach = useResource(
    (signal) => get<CoachDetailData>(`/v1/admin/coaches/${coachId}`, undefined, signal),
    [coachId],
  )
  const reviews = useResource(
    (signal) => get<Review[]>(`/v1/admin/coaches/${coachId}/reviews`, undefined, signal),
    [coachId],
  )

  const reload = () => {
    coach.reload()
    reviews.reload()
  }

  const planColumns: Column<Plan>[] = [
    { key: 'name', header: 'Plan', strong: true, render: (p) => p.name },
    {
      key: 'price',
      header: 'Price',
      render: (p) => `${minor(p.price_minor, p.currency)} / ${p.interval}`,
    },
    {
      key: 'inclusions',
      header: 'What the client is promised',
      render: (p) => p.inclusions.join(' · ') || '—',
    },
    {
      key: 'active',
      header: '',
      render: (p) => (p.active ? <Badge tone="green">Active</Badge> : <Badge>Off</Badge>),
    },
  ]

  const reviewColumns: Column<Review>[] = [
    { key: 'rating', header: 'Rating', strong: true, render: (r) => `${r.rating} / 5` },
    { key: 'body', header: 'Review', render: (r) => r.body || <span className="muted">—</span> },
    {
      key: 'reply',
      header: 'Coach replied',
      render: (r) => (r.reply ? r.reply : <span className="muted">—</span>),
    },
    {
      key: 'months',
      header: 'Subscribed',
      render: (r) => (r.months_subscribed ? `${r.months_subscribed} mo` : '—'),
    },
    { key: 'when', header: 'Written', render: (r) => dateTime(r.created_at) },
    {
      key: 'act',
      header: '',
      render: (r) =>
        canModerate ? (
          <button className="btn btn--sm btn--ghost" onClick={() => setRemoving(r)}>
            Remove…
          </button>
        ) : null,
    },
  ]

  return (
    <Loaded resource={coach}>
      {(data) => (
        <Page
          title={data.display_name || 'Coach'}
          subtitle={
            <>
              <Badge tone={STATUS_TONE[data.status]}>{titleCase(data.status)}</Badge>{' '}
              {titleCase(data.speciality.replace('_', ' '))} ·{' '}
              <Link to="/coaches">back to the queue</Link>
            </>
          }
          actions={
            canModerate && (
              <>
                {data.status === 'draft' && (
                  <button className="btn btn--primary" onClick={() => setDialog('verify')}>
                    Verify and list
                  </button>
                )}
                {data.status === 'suspended' ? (
                  <button className="btn" onClick={() => setDialog('reinstate')}>
                    Reinstate
                  </button>
                ) : (
                  <button className="btn btn--danger" onClick={() => setDialog('suspend')}>
                    Suspend listing
                  </button>
                )}
              </>
            )
          }
        >
          {data.status === 'draft' && (
            <Notice tone="warn">
              <strong>Not listed.</strong> Nobody in the app can find this profile. It stays that
              way until someone checks the claimed credential —{' '}
              <span className="mono">{data.credential || 'none stated'}</span> — and records what
              they checked.
            </Notice>
          )}
          {data.status === 'suspended' && (
            <Notice tone="warn">
              <strong>Suspended.</strong> The profile is out of search. Existing clients keep their
              threads and their paid period: suspending a listing is not a refund, and voiding what
              somebody bought is a separate decision.
            </Notice>
          )}

          <div className="grid grid--kpi">
            <Kpi label="Active clients" value={count(data.active_clients)} tone="cyan" />
            <Kpi
              label="Rating"
              value={data.review_count ? data.rating.toFixed(1) : '—'}
              foot={`${count(data.review_count)} reviews`}
            />
            <Kpi
              label="Verified"
              value={data.verified_at ? 'Yes' : 'No'}
              tone={data.verified_at ? 'green' : 'orange'}
              foot={data.verified_at ? dateTime(data.verified_at) : 'never checked'}
            />
            <Kpi
              label="Listing paid until"
              value={data.listing_paid_until ? dateTime(data.listing_paid_until) : '—'}
            />
            <Kpi label="Client cap" value={count(data.client_cap)} />
          </div>

          <div className="grid grid--2">
            <Card title="Claim" hint="What this person says they are">
              <dl className="kv">
                <dt>Credential</dt>
                <dd className="mono">{data.credential || '— none given —'}</dd>
                <dt>Experience</dt>
                <dd>{count(data.years_experience)} years</dd>
                <dt>Languages</dt>
                <dd>{data.languages.join(', ') || '—'}</dd>
                <dt>Area</dt>
                <dd>{data.service_area || '—'}</dd>
                <dt>Tags</dt>
                <dd>{data.tags.join(', ') || '—'}</dd>
              </dl>
              {data.bio && <p className="muted" style={{ marginBottom: 0 }}>{data.bio}</p>}
            </Card>

            <Card title="Account" hint="How to reach them to check it">
              <dl className="kv">
                <dt>Email</dt>
                <dd className="mono">{data.account.email || '—'}</dd>
                <dt>Name on account</dt>
                <dd>{data.account.display_name || '—'}</dd>
                <dt>Account status</dt>
                <dd>{titleCase(data.account.status || 'unknown')}</dd>
                <dt>Registered</dt>
                <dd>{dateTime(data.created_at)}</dd>
                <dt>Subscriptions</dt>
                <dd>
                  {Object.entries(data.subscriptions_by_status)
                    .map(([k, v]) => `${titleCase(k)} ${v}`)
                    .join(' · ') || 'none'}
                </dd>
              </dl>
              <Notice>
                This console shows who pays this coach and how much. It does not show what any of
                their clients wrote to them — a person describing symptoms to a dietitian is health
                data, and no operator screen reads that table.
              </Notice>
            </Card>
          </div>

          <Card title="Plans" hint="What clients are charged, and what they are promised for it">
            {data.plans.length === 0 ? (
              <Empty>No plans. A listing without one cannot take a client.</Empty>
            ) : (
              <Table columns={planColumns} rows={data.plans} rowKey={(p) => p.id} />
            )}
          </Card>

          <Card title="Reviews" hint="Only clients who paid can write one">
            <Loaded resource={reviews} emptyWhen={(r) => r.length === 0} empty={<Empty>No reviews yet.</Empty>}>
              {(rows) => <Table columns={reviewColumns} rows={rows} rowKey={(r) => r.id} />}
            </Loaded>
          </Card>

          {dialog === 'verify' && (
            <ReasonDialog
              title={`Verify ${data.display_name}`}
              description={
                <>
                  This puts the profile in front of people asking for health advice. Record what you
                  actually checked — a registration number, a certificate seen — because this note
                  is the only evidence the check happened.
                </>
              }
              confirmLabel="Verify and list"
              minLength={10}
              onConfirm={(reason) =>
                run(
                  () => post(`/v1/admin/coaches/${coachId}/verify`, { reason }),
                  'Listed',
                  reload,
                )
              }
              onClose={() => setDialog(null)}
            />
          )}

          {dialog === 'suspend' && (
            <ReasonDialog
              title={`Suspend ${data.display_name}`}
              description="The profile leaves search and stops taking new clients. Existing subscriptions and threads are untouched."
              confirmLabel="Suspend"
              danger
              onConfirm={(reason) =>
                run(
                  () => post(`/v1/admin/coaches/${coachId}/suspend`, { reason }),
                  'Listing suspended',
                  reload,
                )
              }
              onClose={() => setDialog(null)}
            />
          )}

          {dialog === 'reinstate' && (
            <ReasonDialog
              title={`Reinstate ${data.display_name}`}
              description={
                data.verified_at
                  ? 'The profile goes back into search.'
                  : 'This coach was never verified, so reinstating returns them to the draft queue rather than to search.'
              }
              confirmLabel="Reinstate"
              onConfirm={(reason) =>
                run(
                  () => post(`/v1/admin/coaches/${coachId}/reinstate`, { reason }),
                  'Reinstated',
                  reload,
                )
              }
              onClose={() => setDialog(null)}
            />
          )}

          {removing && (
            <ReasonDialog
              title="Remove this review"
              description="The coach's rating is recomputed from what is left. The client is not told."
              confirmLabel="Remove"
              danger
              onConfirm={(reason) =>
                run(
                  () => del(`/v1/admin/reviews/${removing.id}`, { reason }),
                  'Review removed',
                  reload,
                )
              }
              onClose={() => setRemoving(null)}
            />
          )}
        </Page>
      )}
    </Loaded>
  )
}
